import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import api, { apiError } from "../lib/api";
import { track } from "../lib/analytics";
import { useAuth } from "../context/AuthContext";
import { Helper, Tip, Term } from "../components/ui-bits";
import DescribeProject from "../components/DescribeProject";
import { STATES, PROJECT_TYPES, CONNECTIVITY_OPTIONS, VOLTAGE_OPTIONS, SITE_FLAGS, BUSINESS_MODELS } from "../lib/constants";
import { Sparkles, ArrowLeft, ArrowRight, PenLine, ListChecks, MessageSquareText } from "lucide-react";
import { toast } from "sonner";

function OtherOption({ field, prompt, onMatched }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const match = async () => {
    setBusy(true);
    setResult(null);
    try {
      const { data } = await api.post("/wizard/classify", { field, text });
      setResult(data);
      if (data.value) onMatched(data);
    } catch (e) {
      toast.error(apiError(e, "AI matching is unavailable — pick the closest option manually"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-2.5">
      {!open ? (
        <button onClick={() => setOpen(true)} className="w-full text-left rounded-lg border border-dashed border-[#1D4ED8]/40 bg-white/60 px-4 py-3 hover:border-[#1D4ED8] transition-colors" data-testid={`other-option-${field}`}>
          <p className="font-medium text-[#1D4ED8] text-sm flex items-center gap-2"><PenLine size={14} /> Other — describe it in your own words</p>
          <p className="text-xs text-[#475569] mt-0.5">Can't find your case above? Write it and our AI matches it to the closest option.</p>
        </button>
      ) : (
        <div className="rounded-lg border-2 border-[#1D4ED8] bg-white px-4 py-3" data-testid={`other-panel-${field}`}>
          <p className="text-sm font-medium text-[#0B1F3A] flex items-center gap-2"><PenLine size={14} className="text-[#1D4ED8]" /> {prompt}</p>
          <textarea value={text} onChange={(e) => setText(e.target.value)} rows={2}
            placeholder="e.g., a floating solar plant on a reservoir feeding our textile factory nearby"
            className="mt-2 w-full border border-[#0B1F3A]/20 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-[#1D4ED8] focus:outline-none" data-testid={`other-text-${field}`} />
          <div className="flex gap-2 mt-2">
            <button onClick={match} disabled={busy || text.trim().length < 5}
              className="bg-[#1D4ED8] text-white rounded-md px-4 py-2 text-xs font-semibold disabled:opacity-50" data-testid={`other-match-btn-${field}`}>
              {busy ? "Matching…" : "Find the closest match"}
            </button>
            <button onClick={() => { setOpen(false); setResult(null); }} className="text-xs text-[#475569] underline">Close</button>
          </div>
          {result && result.value && (
            <div className="mt-3 bg-emerald-50 border border-[#059669]/40 rounded-md px-3 py-2" data-testid={`other-result-${field}`}>
              <p className="text-sm text-[#0B1F3A]"><span className="font-semibold text-[#059669]">Matched: {result.label}</span></p>
              <p className="text-xs text-[#475569] mt-0.5">{result.reason} — we've selected it above; change it if that's not right.</p>
            </div>
          )}
          {result && !result.value && (
            <p className="mt-3 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">{result.reason}</p>
          )}
        </div>
      )}
    </div>
  );
}

const STORAGE_KEY = "regnav_wizard_v1";
const DEFAULTS = {
  project_type: "", bess_included: "no", capacity_mw: "", capacity_unit: "MW", state: "", district: "",
  connectivity: "", connectivity_confident: true, helper_buyer: "", voltage_kv: "", dedicated_line: "", line_length_km: "",
  land_type: "", forest_land: "no", protected_area: "no", crosses_nh: "no", crosses_sh: "no", crosses_railway: "no",
  crosses_water: "no", airport_zone: "no", defence_nearby: "no", crosses_tl: "no", crossed_tl_voltage: "",
  business_model: "",
};

function SegButtons({ value, onChange, options, testid }) {
  return (
    <div className="flex gap-1.5" data-testid={testid}>
      {options.map((o) => (
        <button key={o.value} type="button" onClick={() => onChange(o.value)}
          className={`px-3 py-1.5 rounded-md text-xs font-medium border transition-colors ${value === o.value ? "bg-[#0B1F3A] text-[#F5F8FF] border-[#0B1F3A]" : "bg-white border-[#0B1F3A]/20 text-[#475569] hover:border-[#059669]"}`}
          data-testid={`${testid}-${o.value}`}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

const YNU = [{ value: "yes", label: "Yes" }, { value: "no", label: "No" }, { value: "unsure", label: "Unsure" }];
const YNNS = [{ value: "yes", label: "Yes" }, { value: "no", label: "No" }, { value: "not_sure", label: "Not sure" }];
const BESS_OPTIONS = [{ value: "yes", label: "Yes, with BESS" }, { value: "no", label: "No" }];
const LAND_OPTIONS = [{ value: "private", label: "Private" }, { value: "government", label: "Government revenue" }, { value: "mixed", label: "Mixed" }, { value: "not_sure", label: "Not sure" }];
const BUYERS = [{ v: "own_use", l: "Own use" }, { v: "same_state", l: "Buyer in same state" }, { v: "another_state", l: "Buyer in another state" }, { v: "not_decided", l: "Not decided" }];

export default function Wizard() {
  const [params] = useSearchParams();
  const editProjectId = params.get("project");
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  const [answers, setAnswers] = useState(() => {
    try {
      const saved = sessionStorage.getItem(STORAGE_KEY);
      if (saved) return { ...DEFAULTS, ...JSON.parse(saved).answers };
    } catch (e) {
      console.warn("wizard: couldn't restore saved answers", e);
    }
    return { ...DEFAULTS };
  });
  const [step, setStep] = useState(() => {
    try {
      const saved = sessionStorage.getItem(STORAGE_KEY);
      if (saved) return JSON.parse(saved).step || 1;
    } catch (e) {
      console.warn("wizard: couldn't restore saved step", e);
    }
    return 1;
  });
  const [mode, setMode] = useState(() => {
    try {
      const saved = sessionStorage.getItem(STORAGE_KEY);
      if (saved) return JSON.parse(saved).mode || null;
    } catch (e) {
      console.warn("wizard: couldn't restore saved mode", e);
    }
    return null;
  });
  const [preview, setPreview] = useState(null);
  const [prevCount, setPrevCount] = useState(0);
  const [generating, setGenerating] = useState(false);
  const [genStage, setGenStage] = useState(0);
  const [helpChoose, setHelpChoose] = useState(false);
  const [suggestion, setSuggestion] = useState(null);
  const debounceRef = useRef(null);
  const [gate, setGate] = useState(false);
  const [analyzerEmail, setAnalyzerEmail] = useState("");
  const [analyzerSent, setAnalyzerSent] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (editProjectId && user) {
      setMode("steps");
      api.get(`/projects/${editProjectId}`).then(({ data }) => {
        setAnswers({ ...DEFAULTS, ...data.answers });
        setStep(1);
      }).catch(() => toast.error("Couldn't load the project — starting fresh"));
    } else if (!user && !editProjectId && localStorage.getItem("regnav_visitor_run_done")) {
      setGate(true);
    }
  }, [loading, user, editProjectId]);

  useEffect(() => {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ answers, step, mode }));
  }, [answers, step, mode]);

  useEffect(() => {
    if (!answers.project_type || answers.project_type === "green_procurement") return;
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const { data } = await api.post("/wizard/preview", { answers });
        setPreview((p) => {
          setPrevCount(p?.approvals_count || 0);
          return data;
        });
      } catch (e) {
        console.debug("wizard: live preview skipped", e);
      }
    }, 350);
    return () => clearTimeout(debounceRef.current);
  }, [answers]);

  const set = (patch) => setAnswers((a) => ({ ...a, ...patch }));

  const suggest = (buyer) => {
    const cap = parseFloat(answers.capacity_mw) || 0;
    let conn = "stu", reason = "";
    if (buyer === "another_state") { conn = "ctu"; reason = `Interstate buyer${cap ? ` + ${cap} MW` : ""} → usually CTU/ISTS`; }
    else if (buyer === "own_use") { conn = cap >= 10 ? "stu" : "captive"; reason = cap >= 10 ? "Own use at scale → usually STU-connected captive" : "Own use at your site → captive behind-the-meter"; }
    else if (buyer === "same_state") { conn = cap >= 10 ? "stu" : "discom"; reason = cap >= 10 ? "In-state buyer at scale → usually STU (intra-state)" : "Smaller in-state supply → usually discom-level"; }
    else { conn = cap >= 50 ? "ctu" : "stu"; reason = cap >= 50 ? `${cap} MW with buyer undecided → most large projects go CTU/ISTS` : "Buyer undecided → state grid is the common starting point"; }
    setSuggestion({ conn, reason });
    set({ helper_buyer: buyer });
  };

  const stepValid = () => {
    if (step === 1) return !!answers.project_type && answers.project_type !== "green_procurement";
    if (step === 2) return !!answers.state;
    if (step === 3) return !!answers.connectivity && answers.connectivity !== "not_sure" && !!answers.voltage_kv && !!answers.dedicated_line;
    if (step === 4) return !!answers.land_type;
    if (step === 5) return !!answers.business_model;
    return false;
  };

  useEffect(() => {
    track("wizard_started");
  }, []);

  const generate = async () => {
    setGenerating(true);
    setGenStage(0);
    const stages = ["Reading your inputs", "Matching against 200+ expert rules", "Building your roadmap"];
    for (let i = 0; i < stages.length; i++) {
      setGenStage(i);
      await new Promise((r) => setTimeout(r, 500));
    }
    // retries transient network drops (no server response) up to 2 times
    const withRetry = async (fn) => {
      for (let attempt = 0; ; attempt++) {
        try {
          return await fn();
        } catch (e) {
          if (e.response || attempt >= 2) throw e;
          await new Promise((r) => setTimeout(r, 1200 * (attempt + 1)));
        }
      }
    };
    try {
      if (editProjectId && user) {
        const { data } = await withRetry(() => api.put(`/projects/${editProjectId}`, { answers }));
        sessionStorage.removeItem(STORAGE_KEY);
        navigate(`/projects/${editProjectId}`, { state: { changes: data.changes } });
      } else {
        const { data } = await withRetry(() => api.post("/roadmap/generate", { answers }));
        track("wizard_completed");
        if (!user) localStorage.setItem("regnav_visitor_run_done", "1");
        sessionStorage.removeItem(STORAGE_KEY);
        navigate(`/roadmap/${data.id}`);
      }
    } catch (e) {
      setGenerating(false);
      const msg = e.response
        ? apiError(e, "Couldn't generate the roadmap — try again")
        : "Network hiccup while building your roadmap — your answers are safe, just hit Retry.";
      toast.error(msg, {
        action: { label: "Retry", onClick: generate },
      });
    }
  };

  const sendAnalyzerLead = async (e) => {
    e.preventDefault();
    await api.post("/leads", { type: "analyzer_interest", email: analyzerEmail, note: "Green power procurement — Switch Analyzer waitlist" });
    setAnalyzerSent(true);
  };

  const previewBox = (compact) => (
    <div className={compact
      ? "flex items-center justify-between gap-3 band-navy text-white rounded-lg px-4 py-3"
      : "band-navy text-white rounded-xl p-5 sticky top-20"} data-testid={compact ? "wizard-preview-mobile" : "wizard-preview-sidebar"}>
      <div>
        <p className="text-[10px] uppercase tracking-[0.2em] text-[#34D399] font-semibold flex items-center gap-1"><Sparkles size={11} /> Forming live</p>
        {preview && preview.approvals_count > 0 ? (
          <p className={`font-serif ${compact ? "text-base" : "text-xl mt-2"}`} data-testid="wizard-preview-counts">
            <span className={`text-[#34D399] font-bold ${prevCount !== preview.approvals_count ? "fade-up inline-block" : ""}`}>{preview.approvals_count} approvals</span>
            <span> identified · {preview.departments_count} departments</span>
          </p>
        ) : (
          <p className={`text-[#F5F8FF]/60 ${compact ? "text-xs" : "text-sm mt-2"}`}>Answer the steps — your approval count builds as you go.</p>
        )}
      </div>
      {!compact && preview?.approvals_count > 0 && (
        <p className="text-xs text-[#F5F8FF]/60 mt-3">Site flags in step 4 usually add more — nothing gets missed.</p>
      )}
    </div>
  );

  if (gate) return (
    <div className="max-w-md mx-auto px-4 py-16 text-center">
      <h1 className="font-serif text-2xl text-[#0B1F3A]">You've used your free visitor roadmap</h1>
      <p className="text-sm text-[#475569] mt-2">Create a free account for unlimited roadmaps, saved projects and PDFs — it takes 30 seconds.</p>
      <div className="space-y-2 mt-6">
        <Link to="/signup?next=/wizard" className="block bg-[#1D4ED8] text-white rounded-md py-3 text-sm font-medium" data-testid="gate-signup-btn">Create free account</Link>
        <Link to="/login?next=/wizard" className="block border border-[#0B1F3A]/30 rounded-md py-3 text-sm font-medium text-[#0B1F3A]" data-testid="gate-login-btn">Log in</Link>
      </div>
    </div>
  );

  if (generating) return (
    <div className="max-w-md mx-auto px-4 py-24" data-testid="wizard-generating">
      <div className="space-y-4">
        {["Reading your inputs", "Matching against 200+ expert rules", "Building your roadmap"].map((s, i) => (
          <div key={s} className={`flex items-center gap-3 transition-opacity ${i <= genStage ? "opacity-100" : "opacity-30"}`}>
            <div className={`w-2.5 h-2.5 rounded-full ${i < genStage ? "bg-[#059669]" : i === genStage ? "bg-[#059669] animate-pulse" : "bg-[#0B1F3A]/20"}`} />
            <p className="font-serif text-[#0B1F3A]">{s}{i === genStage ? "…" : i < genStage ? " ✓" : ""}</p>
          </div>
        ))}
      </div>
    </div>
  );

  const chooseMode = (m) => {
    setMode(m);
    track("wizard_mode", { mode: m });
  };

  if (!editProjectId && !mode) return (
    <div className="max-w-2xl mx-auto px-4 py-14" data-testid="wizard-mode-choice">
      <h1 className="font-serif text-2xl sm:text-3xl text-[#0B1F3A]">How would you like to start?</h1>
      <p className="text-sm text-[#475569] mt-2">Both paths end in the same expert-verified roadmap — pick whichever feels natural.</p>
      <div className="grid sm:grid-cols-2 gap-4 mt-8">
        <button onClick={() => chooseMode("steps")} data-testid="mode-steps-btn"
          className="text-left rounded-xl border-2 border-[#0B1F3A]/15 bg-white p-5 hover:border-[#059669] transition-colors">
          <ListChecks className="text-[#1D4ED8]" size={22} />
          <p className="font-serif font-semibold text-[#0B1F3A] mt-3">Answer step by step</p>
          <p className="text-xs text-[#475569] mt-1">5 quick questions with helpful defaults — about 60 seconds.</p>
        </button>
        <button onClick={() => chooseMode("describe")} data-testid="mode-describe-btn"
          className="relative text-left rounded-xl border-2 border-[#0B1F3A]/15 bg-white p-5 hover:border-[#059669] transition-colors">
          <span className="absolute top-3 right-3 text-[10px] font-bold uppercase tracking-wider text-[#059669] bg-emerald-50 border border-[#059669]/30 rounded-full px-2 py-0.5">AI-assisted</span>
          <MessageSquareText className="text-[#059669]" size={22} />
          <p className="font-serif font-semibold text-[#0B1F3A] mt-3">Describe your project in your own words</p>
          <p className="text-xs text-[#475569] mt-1">Write it once — our AI fills the form and asks only what's missing.</p>
        </button>
      </div>
    </div>
  );

  if (!editProjectId && mode === "describe") return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 pb-28 lg:pb-8">
      <div className="grid lg:grid-cols-[1fr_300px] gap-8">
        <DescribeProject answers={answers} set={set} onGenerate={generate} onSwitchMode={() => setMode("steps")} />
        <div className="hidden lg:block">{previewBox(false)}</div>
      </div>
      <div className="lg:hidden fixed bottom-0 left-0 right-0 z-40 px-3 pb-3">{previewBox(true)}</div>
    </div>
  );

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 pb-28 lg:pb-8">
      <div className="max-w-2xl">
        <div className="flex items-center justify-between gap-3">
          <p className="text-sm text-[#475569]" data-testid="wizard-progress-label">Step {step} of 5 · about 60 seconds</p>
          {!editProjectId && step === 1 && (
            <button onClick={() => setMode("describe")} className="text-xs text-[#1D4ED8] underline shrink-0" data-testid="steps-switch-describe">
              Prefer to write it in one go?
            </button>
          )}
        </div>
        <div className="h-1 w-full bg-[#0B1F3A]/10 rounded-full overflow-hidden mt-2">
          <div className="h-full bg-[#059669] transition-all duration-500" style={{ width: `${(step / 5) * 100}%` }} />
        </div>
      </div>

      <div className="grid lg:grid-cols-[1fr_300px] gap-8 mt-6">
        <div className="max-w-2xl">
          {step === 1 && (
            <div className="fade-up" data-testid="wizard-step-1">
              <h1 className="font-serif text-2xl sm:text-3xl text-[#0B1F3A]">What are you building?</h1>
              <Helper>Pick the closest match — you can refine everything later.</Helper>
              <div className="space-y-2.5 mt-6">
                {PROJECT_TYPES.map((pt) => (
                  <button key={pt.value} onClick={() => set({ project_type: pt.value })}
                    className={`w-full text-left rounded-lg border px-4 py-3 transition-colors ${answers.project_type === pt.value ? "border-[#059669] border-2 bg-white" : "border-[#0B1F3A]/15 bg-white/60 hover:border-[#059669]/60"}`}
                    data-testid={`project-type-${pt.value}`}>
                    <p className="font-medium text-[#0B1F3A] text-sm">{pt.label}</p>
                    <p className="text-xs text-[#475569] mt-0.5">{pt.help}</p>
                  </button>
                ))}
              </div>
              <OtherOption field="project_type" prompt="Describe what you're building" onMatched={(r) => set({ project_type: r.value })} />
              {answers.project_type === "hybrid" && (
                <div className="mt-4 bg-white border border-[#0B1F3A]/15 rounded-lg px-4 py-3">
                  <p className="text-sm font-medium text-[#0B1F3A]">Does the hybrid include battery storage (<Term term="BESS">BESS</Term>)?</p>
                  <div className="mt-2"><SegButtons value={answers.bess_included} onChange={(v) => set({ bess_included: v })} options={BESS_OPTIONS} testid="bess-toggle" /></div>
                </div>
              )}
              {answers.project_type === "green_procurement" && (
                <div className="mt-4 bg-white border-2 border-[#059669] rounded-lg px-5 py-4" data-testid="analyzer-coming-soon">
                  <p className="font-serif font-semibold text-[#0B1F3A]">The Green Power Switch Analyzer is coming in days</p>
                  <p className="text-sm text-[#475569] mt-1">It compares own-solar EPC vs PPA vs staying on discom power, with day-one savings. Leave your email and we'll notify you the moment it's live.</p>
                  {analyzerSent ? (
                    <p className="text-sm text-[#059669] font-medium mt-3" data-testid="analyzer-lead-done">You're on the list — we'll be in touch.</p>
                  ) : (
                    <form onSubmit={sendAnalyzerLead} className="flex gap-2 mt-3">
                      <input type="email" required value={analyzerEmail} onChange={(e) => setAnalyzerEmail(e.target.value)} placeholder="Work email"
                        className="flex-1 border border-[#0B1F3A]/20 rounded-md px-3 py-2 text-sm" data-testid="analyzer-email-input" />
                      <button type="submit" className="bg-[#1D4ED8] text-white rounded-md px-4 py-2 text-sm font-medium" data-testid="analyzer-notify-btn">Notify me</button>
                    </form>
                  )}
                </div>
              )}
            </div>
          )}

          {step === 2 && (
            <div className="fade-up" data-testid="wizard-step-2">
              <h1 className="font-serif text-2xl sm:text-3xl text-[#0B1F3A]">Scale & location</h1>
              <div className="space-y-5 mt-6">
                <div>
                  <label className="text-sm font-medium text-[#0B1F3A]">Capacity ({answers.project_type === "bess" ? "MWh" : "MW"})<Tip text="The planned installed capacity of your project." example="200 for a 200 MW solar plant" /></label>
                  <input type="number" min="0" value={answers.capacity_mw} onChange={(e) => set({ capacity_mw: e.target.value, capacity_unit: answers.project_type === "bess" ? "MWh" : "MW" })}
                    className="mt-1 w-full sm:w-56 bg-white border border-[#0B1F3A]/20 rounded-md px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#059669] focus:outline-none" data-testid="capacity-input" />
                  <Helper>An approximate figure is fine.</Helper>
                </div>
                <div>
                  <label className="text-sm font-medium text-[#0B1F3A]">State<Tip text="The state where the project (and most of the line route) sits — it decides which state authorities appear in your roadmap." example="Rajasthan" /></label>
                  <select value={answers.state} onChange={(e) => set({ state: e.target.value })}
                    className="mt-1 w-full sm:w-72 bg-white border border-[#0B1F3A]/20 rounded-md px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#059669] focus:outline-none" data-testid="state-select">
                    <option value="">Select state…</option>
                    {STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                  <Helper>All states supported — we're transparent about how deep our state-specific coverage goes.</Helper>
                </div>
                <div>
                  <label className="text-sm font-medium text-[#0B1F3A]">District</label>
                  <input value={answers.district} onChange={(e) => set({ district: e.target.value })}
                    className="mt-1 w-full sm:w-72 bg-white border border-[#0B1F3A]/20 rounded-md px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#059669] focus:outline-none" data-testid="district-input" />
                  <Helper>Optional — used for labels and location flags.</Helper>
                </div>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="fade-up" data-testid="wizard-step-3">
              <h1 className="font-serif text-2xl sm:text-3xl text-[#0B1F3A]">Where will your project connect to the grid?</h1>
              <Helper>This single answer decides which transmission authority runs your connectivity approvals.</Helper>
              <div className="space-y-2.5 mt-6">
                {CONNECTIVITY_OPTIONS.map((c) => (
                  <button key={c.value} onClick={() => {
                    if (c.value === "not_sure") { setHelpChoose(true); setSuggestion(null); set({ connectivity: "" }); }
                    else { setHelpChoose(false); setSuggestion(null); set({ connectivity: c.value, connectivity_confident: true }); }
                  }}
                    className={`w-full text-left rounded-lg border px-4 py-3 transition-colors ${(answers.connectivity === c.value || (c.value === "not_sure" && helpChoose)) ? "border-[#059669] border-2 bg-white" : "border-[#0B1F3A]/15 bg-white/60 hover:border-[#059669]/60"}`}
                    data-testid={`connectivity-${c.value}`}>
                    <p className="font-medium text-[#0B1F3A] text-sm">{c.label}</p>
                    <p className="text-xs text-[#475569] mt-0.5">{c.help}</p>
                  </button>
                ))}
              </div>
              <OtherOption field="connectivity" prompt="Describe your grid situation or who buys the power" onMatched={(r) => { set({ connectivity: r.value, connectivity_confident: true }); setHelpChoose(false); }} />

              {helpChoose && (
                <div className="mt-4 bg-white border-2 border-[#059669] rounded-lg px-5 py-4" data-testid="help-me-choose">
                  <p className="font-medium text-[#0B1F3A] text-sm">Quick question: who will buy the power?</p>
                  <div className="grid grid-cols-2 gap-2 mt-3">
                    {BUYERS.map((b) => (
                      <button key={b.v} onClick={() => suggest(b.v)}
                        className={`rounded-md border px-3 py-2 text-xs font-medium ${answers.helper_buyer === b.v ? "bg-[#0B1F3A] text-[#F5F8FF] border-[#0B1F3A]" : "border-[#0B1F3A]/20 text-[#475569] hover:border-[#059669]"}`}
                        data-testid={`buyer-${b.v}`}>{b.l}</button>
                    ))}
                  </div>
                  {suggestion && (
                    <div className="mt-4 bg-[#F5F8FF] rounded-md px-4 py-3" data-testid="connectivity-suggestion">
                      <p className="text-sm text-[#0B1F3A]"><span className="font-semibold">Our suggestion: {CONNECTIVITY_OPTIONS.find((c) => c.value === suggestion.conn)?.label}</span></p>
                      <p className="text-xs text-[#475569] mt-1">{suggestion.reason}</p>
                      <div className="flex flex-wrap gap-2 mt-3">
                        <button onClick={() => { set({ connectivity: suggestion.conn, connectivity_confident: true }); setHelpChoose(false); }}
                          className="bg-[#1D4ED8] text-white rounded-md px-4 py-2 text-xs font-medium" data-testid="suggestion-confirm-btn">Sounds right — use it</button>
                        <button onClick={() => { set({ connectivity: suggestion.conn, connectivity_confident: false }); setHelpChoose(false); }}
                          className="border border-[#0B1F3A]/30 rounded-md px-4 py-2 text-xs font-medium text-[#0B1F3A]" data-testid="suggestion-unsure-btn">Still unsure — proceed & mark 'Conditional'</button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="mt-6 space-y-5">
                <div>
                  <label className="text-sm font-medium text-[#0B1F3A]">Voltage level<Tip text="The voltage at your interconnection point." example="220 kV for a 200 MW plant on the national grid" /></label>
                  <select value={answers.voltage_kv} onChange={(e) => set({ voltage_kv: e.target.value })}
                    className="mt-1 w-full sm:w-72 bg-white border border-[#0B1F3A]/20 rounded-md px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#059669] focus:outline-none" data-testid="voltage-select">
                    <option value="">Select…</option>
                    {VOLTAGE_OPTIONS.map((v) => <option key={v} value={v}>{v === "not_sure" ? "Not sure" : `${v} kV`}</option>)}
                  </select>
                  <Helper>Check your connectivity application or ask your electrical consultant — 'Not sure' marks voltage-dependent steps as Conditional.</Helper>
                </div>
                <div>
                  <label className="text-sm font-medium text-[#0B1F3A]">Dedicated transmission line required?<Tip text="A new line built just to evacuate your project's power to the grid substation." example="A 25 km 220 kV line from your plant to the nearest grid substation" /></label>
                  <div className="mt-2"><SegButtons value={answers.dedicated_line} onChange={(v) => set({ dedicated_line: v })} options={YNNS} testid="dedicated-line" /></div>
                  <Helper>A new line built just to evacuate your project's power.</Helper>
                  {answers.dedicated_line === "yes" && (
                    <div className="mt-3">
                      <label className="text-xs font-medium text-[#0B1F3A]">Approximate length (km)</label>
                      <input type="number" min="0" value={answers.line_length_km} onChange={(e) => set({ line_length_km: e.target.value })}
                        className="mt-1 w-40 bg-white border border-[#0B1F3A]/20 rounded-md px-3 py-2 text-sm" data-testid="line-length-input" />
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {step === 4 && (
            <div className="fade-up" data-testid="wizard-step-4">
              <h1 className="font-serif text-2xl sm:text-3xl text-[#0B1F3A]">Site & route conditions</h1>
              <Helper>Answer for the project site AND the line route. If you're not certain, pick 'Unsure' — we'll include the approval marked 'Conditional — verify' so nothing is missed.</Helper>
              <div className="mt-6 bg-white border border-[#0B1F3A]/15 rounded-lg px-4 py-3">
                <p className="text-sm font-medium text-[#0B1F3A]">Land type<Tip text="Who owns the project land today — it decides the land approvals path." example="Government revenue land allotted by the Collector" /></p>
                <div className="mt-2"><SegButtons value={answers.land_type} onChange={(v) => set({ land_type: v })}
                  options={LAND_OPTIONS} testid="land-type" /></div>
                <Helper>Private land usually needs NA conversion; government land needs allotment.</Helper>
              </div>
              <div className="mt-3 space-y-2.5">
                {SITE_FLAGS.map((f) => (
                  <div key={f.field} className="bg-white border border-[#0B1F3A]/15 rounded-lg px-4 py-3 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                    <div>
                      <p className="text-sm font-medium text-[#0B1F3A]">{f.glossary ? <>{f.label.split(f.glossary)[0]}<Term term={f.glossary}>{f.glossary}</Term>{f.label.split(f.glossary)[1]}</> : f.label}<Tip text={f.help} /></p>
                    </div>
                    <SegButtons value={answers[f.field]} onChange={(v) => set({ [f.field]: v })} options={YNU} testid={`flag-${f.field}`} />
                  </div>
                ))}
                {answers.crosses_tl === "yes" && (
                  <div className="bg-white border border-[#0B1F3A]/15 rounded-lg px-4 py-3">
                    <label className="text-sm font-medium text-[#0B1F3A]">Voltage of the crossed line (kV, if known)</label>
                    <input value={answers.crossed_tl_voltage} onChange={(e) => set({ crossed_tl_voltage: e.target.value })}
                      className="mt-1 w-40 bg-white border border-[#0B1F3A]/20 rounded-md px-3 py-2 text-sm" data-testid="crossed-tl-voltage-input" />
                    <Helper>Leave blank if unknown — the owner utility confirms it during design.</Helper>
                  </div>
                )}
              </div>
            </div>
          )}

          {step === 5 && (
            <div className="fade-up" data-testid="wizard-step-5">
              <h1 className="font-serif text-2xl sm:text-3xl text-[#0B1F3A]">How will the power be used?</h1>
              <Helper>This shapes advisory notes — it never blocks your roadmap.</Helper>
              <div className="space-y-2.5 mt-6">
                {BUSINESS_MODELS.map((b) => (
                  <button key={b.value} onClick={() => set({ business_model: b.value })}
                    className={`w-full text-left rounded-lg border px-4 py-3 transition-colors ${answers.business_model === b.value ? "border-[#059669] border-2 bg-white" : "border-[#0B1F3A]/15 bg-white/60 hover:border-[#059669]/60"}`}
                    data-testid={`business-model-${b.value}`}>
                    <p className="font-medium text-[#0B1F3A] text-sm">{b.label}</p>
                    <p className="text-xs text-[#475569] mt-0.5">{b.help}</p>
                  </button>
                ))}
              </div>
              <OtherOption field="business_model" prompt="Describe how the power will be used or sold" onMatched={(r) => set({ business_model: r.value })} />
            </div>
          )}

          <div className="flex items-center justify-between mt-8">
            {step > 1 ? (
              <button onClick={() => setStep(step - 1)} className="inline-flex items-center gap-1.5 text-sm font-medium text-[#475569] hover:text-[#0B1F3A]" data-testid="wizard-back-btn">
                <ArrowLeft size={15} /> Back
              </button>
            ) : <span />}
            {step < 5 ? (
              <button onClick={() => setStep(step + 1)} disabled={!stepValid()}
                className="inline-flex items-center gap-2 bg-[#1D4ED8] text-white rounded-md px-6 py-3 text-sm font-medium hover:bg-[#1638A8] disabled:opacity-40 disabled:cursor-not-allowed transition-transform hover:-translate-y-0.5" data-testid="wizard-next-btn">
                Continue <ArrowRight size={15} />
              </button>
            ) : (
              <button onClick={generate} disabled={!stepValid()}
                className="inline-flex items-center gap-2 bg-[#059669] text-white rounded-md px-6 py-3 text-sm font-bold hover:bg-[#059669]/90 disabled:opacity-40 transition-transform hover:-translate-y-0.5" data-testid="wizard-generate-btn">
                {editProjectId ? "Regenerate my roadmap" : "Build my roadmap"} <ArrowRight size={15} />
              </button>
            )}
          </div>
        </div>

        <div className="hidden lg:block">{previewBox(false)}</div>
      </div>

      <div className="lg:hidden fixed bottom-0 left-0 right-0 z-40 px-3 pb-3">{previewBox(true)}</div>
    </div>
  );
}
